import './styles.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';
import {
  confirmBulkImport,
  getBulkImportCatalogue,
  getBulkImportTemplate,
  validateBulkImportFile
} from '../../services/api/bulk-import.api';
import { createGoogleSheetFromCsv } from '../../services/api/google-drive.api';
import GoogleDriveTemplateButton from '../../components/GoogleDriveTemplateButton';
import { handleFileDownload } from '../../shared/utils/file-download';

const responseData = (response) => response?.data ?? response;

const requestIssues = (error, fallbackMessage) => {
  const errors = error?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length) return errors;
  return [{ code: 'request_failed', message: fallbackMessage }];
};

const valueLabel = (value) => {
  if (value === undefined || value === null || value === '') return '—';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const Summary = ({ summary = null, mode }) => {
  if (!summary) return null;
  const counts =
    mode === 'validation'
      ? [
          ['Total rows', summary.totalRows],
          ['Valid rows', summary.validRows],
          ['Invalid rows', summary.invalidRows]
        ]
      : [
          ['Total rows', summary.totalRows],
          ['Created rows', summary.createdRows],
          ['Rejected rows', summary.rejectedRows]
        ];

  return (
    <Box className="bulk-import-summary" aria-label={`${mode} summary`}>
      {counts.map(([label, count]) => (
        <Box className="bulk-import-summary-item" key={label}>
          <Typography component="span" className="bulk-import-summary-count">
            {count ?? 0}
          </Typography>
          <Typography component="span" variant="body2">
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

Summary.propTypes = {
  summary: PropTypes.shape({
    totalRows: PropTypes.number,
    validRows: PropTypes.number,
    invalidRows: PropTypes.number,
    createdRows: PropTypes.number,
    rejectedRows: PropTypes.number
  }),
  mode: PropTypes.oneOf(['validation', 'import']).isRequired
};

const IssueTable = ({ title, issues = [], severity }) => {
  if (!issues?.length) return null;

  return (
    <Box className="bulk-import-issues">
      <Typography component="h3" variant="subtitle1">
        {title} ({issues.length})
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>Column</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>What needs to be fixed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {issues.map((issue, index) => (
              <TableRow
                key={`${issue.code}-${issue.row ?? 'file'}-${issue.column ?? ''}-${index}`}
              >
                <TableCell>{issue.row ?? 'File'}</TableCell>
                <TableCell>{issue.column ?? '—'}</TableCell>
                <TableCell className="bulk-import-issue-value">
                  {valueLabel(issue.value)}
                </TableCell>
                <TableCell>
                  <Typography
                    color={severity === 'error' ? 'error' : 'textPrimary'}
                    variant="body2"
                  >
                    {issue.message}
                  </Typography>
                  {issue.suggestions?.length > 0 && (
                    <Typography variant="caption" color="textSecondary">
                      Suggestions: {issue.suggestions.join(', ')}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

IssueTable.propTypes = {
  title: PropTypes.string.isRequired,
  issues: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      row: PropTypes.number,
      column: PropTypes.string,
      value: PropTypes.any,
      suggestions: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  severity: PropTypes.oneOf(['error', 'warning']).isRequired
};

const PreviewTable = ({ rows = [] }) => {
  const headers = useMemo(
    () => [...new Set(rows.flatMap((row) => Object.keys(row)))],
    [rows]
  );
  if (!rows.length) return null;

  return (
    <Box className="bulk-import-preview">
      <Typography component="h3" variant="subtitle1">
        Readable preview ({rows.length} {rows.length === 1 ? 'row' : 'rows'})
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Readable import preview">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              {headers.map((header) => (
                <TableCell key={header}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={`preview-${index}`}>
                <TableCell>{index + 1}</TableCell>
                {headers.map((header) => (
                  <TableCell key={header}>{valueLabel(row[header])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

PreviewTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object)
};

const BusyButtonContent = ({ busy, children }) => (
  <>
    {busy && <CircularProgress size={18} color="inherit" aria-hidden="true" />}
    {children}
  </>
);

BusyButtonContent.propTypes = {
  busy: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired
};

const BulkImport = () => {
  const [catalogue, setCatalogue] = useState(null);
  const [catalogueLoading, setCatalogueLoading] = useState(true);
  const [catalogueIssues, setCatalogueIssues] = useState([]);
  const [importType, setImportType] = useState('');
  const [referenceDataCategory, setReferenceDataCategory] = useState('');
  const [file, setFile] = useState(null);
  const [fileIssue, setFileIssue] = useState(null);
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);
  const [actionIssues, setActionIssues] = useState([]);
  const [templateFeedback, setTemplateFeedback] = useState(null);
  const [confirmAcknowledged, setConfirmAcknowledged] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [dropActive, setDropActive] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const loadCatalogue = useCallback(async () => {
    setCatalogueLoading(true);
    setCatalogueIssues([]);
    try {
      const loadedCatalogue = responseData(await getBulkImportCatalogue());
      if (!Array.isArray(loadedCatalogue?.imports)) {
        throw new Error('The bulk import catalogue response is invalid.');
      }
      setCatalogue(loadedCatalogue);
    } catch (error) {
      setCatalogueIssues(
        requestIssues(
          error,
          'The bulk import catalogue could not be loaded. Try again.'
        )
      );
    } finally {
      setCatalogueLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalogue();
  }, [loadCatalogue]);

  const definitions = useMemo(
    () =>
      catalogue?.imports?.filter(
        (definition) => definition.release === 'initial'
      ) ?? [],
    [catalogue]
  );
  const definition = useMemo(
    () => definitions.find((candidate) => candidate.importType === importType),
    [definitions, importType]
  );
  const needsReferenceDataCategory = importType === 'reference-data';
  const maximumSizeBytes = catalogue?.file?.maximumSizeBytes ?? 512 * 1024;
  const validationIsValid = validation?.status === 'valid';
  const isBusy = Boolean(busyAction);
  const canValidate =
    Boolean(importType && file && !fileIssue) &&
    (!needsReferenceDataCategory || Boolean(referenceDataCategory)) &&
    !isBusy;
  const canConfirm =
    validationIsValid && confirmAcknowledged && !result && !isBusy;

  const clearOutcome = () => {
    setValidation(null);
    setResult(null);
    setActionIssues([]);
    setConfirmAcknowledged(false);
  };

  const inspectFile = (selectedFile) => {
    if (!selectedFile) return 'Select one CSV file.';
    if (!selectedFile.name.toLowerCase().endsWith('.csv'))
      return 'Only CSV files are accepted.';
    if (selectedFile.size > maximumSizeBytes) {
      return `The file exceeds the ${Math.round(maximumSizeBytes / 1024)} KB limit.`;
    }
    return null;
  };

  const selectFile = (selectedFile) => {
    clearOutcome();
    setFile(selectedFile ?? null);
    setFileIssue(inspectFile(selectedFile));
  };

  const handleImportTypeChange = (event) => {
    clearOutcome();
    setTemplateFeedback(null);
    setImportType(event.target.value);
    setReferenceDataCategory('');
  };

  const handleCategoryChange = (event) => {
    clearOutcome();
    setTemplateFeedback(null);
    setReferenceDataCategory(event.target.value);
  };

  const handleValidate = async () => {
    if (!canValidate) return;
    setBusyAction('validate');
    setActionIssues([]);
    setResult(null);
    setConfirmAcknowledged(false);
    try {
      const response = await validateBulkImportFile(
        file,
        importType,
        needsReferenceDataCategory ? referenceDataCategory : undefined
      );
      const payload = responseData(response);
      if (!payload || !['valid', 'invalid'].includes(payload.status)) {
        throw new Error('The validation response is invalid.');
      }
      setValidation(payload);
    } catch (error) {
      setValidation(null);
      setActionIssues(
        requestIssues(error, 'The CSV could not be validated. Try again.')
      );
    } finally {
      setBusyAction(null);
    }
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setBusyAction('confirm');
    setActionIssues([]);
    try {
      const payload = responseData(
        await confirmBulkImport(validation.validationId)
      );
      if (!payload || !['completed', 'failed'].includes(payload.status)) {
        throw new Error('The import response is invalid.');
      }
      setResult(payload);
    } catch (error) {
      const failedResult = error?.response?.data;
      if (failedResult?.status === 'failed') {
        setResult(failedResult);
      } else {
        setActionIssues(
          requestIssues(
            error,
            'The import could not be confirmed. Revalidate the file and try again.'
          )
        );
      }
    } finally {
      setBusyAction(null);
    }
  };

  const handleTemplateDownload = async () => {
    if (!importType || isBusy) return;
    setBusyAction('template');
    setActionIssues([]);
    setTemplateFeedback(null);
    let objectUrl;
    try {
      const response = await getBulkImportTemplate(importType);
      const content = responseData(response);
      const blob =
        content instanceof Blob
          ? content
          : new Blob([content], { type: 'text/csv' });
      objectUrl = URL.createObjectURL(blob);
      handleFileDownload(objectUrl, `${importType}-template-v1.csv`);
    } catch (error) {
      setTemplateFeedback({
        severity: 'error',
        message: requestIssues(
          error,
          'The CSV template could not be downloaded.'
        )[0].message
      });
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setBusyAction(null);
    }
  };

  const handleDriveAuthorizationStart = () => {
    if (!importType || isBusy) return;
    setBusyAction('drive-template');
    setActionIssues([]);
    setTemplateFeedback(null);
  };

  const handleDriveAuthorizationError = () => {
    setBusyAction(null);
    setTemplateFeedback({
      severity: 'error',
      message:
        'Google Drive authorization was cancelled or could not be completed. No file was created.'
    });
  };

  const handleTemplateSaveToDrive = async (accessToken) => {
    if (!importType) {
      setBusyAction(null);
      return;
    }

    setBusyAction('drive-template');
    setTemplateFeedback(null);
    try {
      const response = await getBulkImportTemplate(importType);
      const content = responseData(response);
      const csvContent =
        content instanceof Blob ? await content.text() : String(content);
      const templateLabel =
        needsReferenceDataCategory && referenceDataCategory
          ? referenceDataCategory
          : (definition?.label ?? importType);
      const driveFile = await createGoogleSheetFromCsv({
        accessToken,
        csvContent,
        fileName: `${templateLabel} - Bulk Import Template v1`
      });

      setTemplateFeedback({
        severity: 'success',
        message: `${driveFile.name} was saved to My Drive as a Google Sheet. When it is ready, download it from Google Sheets as CSV and upload it in step 2.`,
        link: driveFile.webViewLink
      });
    } catch (error) {
      setTemplateFeedback({
        severity: 'error',
        message:
          error?.code?.startsWith('google_drive_') && error.message
            ? error.message
            : requestIssues(
                error,
                'The template could not be saved to Google Drive.'
              )[0].message
      });
    } finally {
      setBusyAction(null);
    }
  };

  const clearFile = () => {
    clearOutcome();
    setFile(null);
    setFileIssue(null);
    setFileInputKey((current) => current + 1);
  };

  const startOver = () => {
    clearFile();
    setImportType('');
    setReferenceDataCategory('');
  };

  return (
    <Paper className="bulk-import-root">
      <Box className="bulk-import-content">
        <Box className="bulk-import-header">
          <Box>
            <Typography variant="h5" component="h1">
              Bulk Import
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Choose what to create, validate one CSV, review the feedback, then
              confirm the import.
            </Typography>
          </Box>
          {(validation || result) && (
            <Button
              startIcon={<RefreshIcon />}
              onClick={startOver}
              disabled={isBusy}
            >
              Start over
            </Button>
          )}
        </Box>

        {catalogueLoading && (
          <Box className="bulk-import-loading" role="status">
            <CircularProgress size={24} />
            <Typography>Loading import options…</Typography>
          </Box>
        )}

        <IssueTable
          title="Catalogue errors"
          issues={catalogueIssues}
          severity="error"
        />
        {catalogueIssues.length > 0 && (
          <Button onClick={loadCatalogue} disabled={catalogueLoading}>
            Retry
          </Button>
        )}

        {catalogue && (
          <>
            <Box className="bulk-import-step">
              <Chip label="1" className="bulk-import-step-number" />
              <Box className="bulk-import-step-content">
                <Typography variant="h6" component="h2">
                  Choose an import type
                </Typography>
                <Box className="bulk-import-controls">
                  <FormControl fullWidth>
                    <InputLabel id="bulk-import-type-label">
                      Import type
                    </InputLabel>
                    <Select
                      labelId="bulk-import-type-label"
                      id="bulk-import-type"
                      value={importType}
                      label="Import type"
                      onChange={handleImportTypeChange}
                      disabled={isBusy}
                    >
                      {definitions.map((item) => (
                        <MenuItem key={item.importType} value={item.importType}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {needsReferenceDataCategory && (
                    <FormControl fullWidth>
                      <InputLabel id="reference-data-category-label">
                        Reference Data category
                      </InputLabel>
                      <Select
                        labelId="reference-data-category-label"
                        id="reference-data-category"
                        value={referenceDataCategory}
                        label="Reference Data category"
                        onChange={handleCategoryChange}
                        disabled={isBusy}
                      >
                        {(definition?.variants ?? []).map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                {definition && (
                  <Box className="bulk-import-definition">
                    <Typography variant="body2">
                      {definition.description}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Duplicate identity:{' '}
                      {definition.duplicateIdentity.join(' + ')}
                    </Typography>
                  </Box>
                )}
                <Box className="bulk-import-template-actions">
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleTemplateDownload}
                    disabled={!importType || isBusy}
                  >
                    <BusyButtonContent busy={busyAction === 'template'}>
                      Download selected template
                    </BusyButtonContent>
                  </Button>
                  <GoogleDriveTemplateButton
                    disabled={!importType || isBusy}
                    onAuthorizationStart={handleDriveAuthorizationStart}
                    onAuthorizationError={handleDriveAuthorizationError}
                    onAuthorized={handleTemplateSaveToDrive}
                  >
                    <BusyButtonContent busy={busyAction === 'drive-template'}>
                      Save selected template to My Drive
                    </BusyButtonContent>
                  </GoogleDriveTemplateButton>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Google will ask for permission to create this Sheet. Proximity
                  can access only files it creates or that you explicitly share
                  with it.
                </Typography>
                {templateFeedback && (
                  <Alert severity={templateFeedback.severity}>
                    {templateFeedback.message}{' '}
                    {templateFeedback.link && (
                      <Link
                        href={templateFeedback.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in Google Sheets
                      </Link>
                    )}
                  </Alert>
                )}
              </Box>
            </Box>

            <Divider />

            <Box className="bulk-import-step">
              <Chip label="2" className="bulk-import-step-number" />
              <Box className="bulk-import-step-content">
                <Typography variant="h6" component="h2">
                  Select and validate a CSV
                </Typography>
                <Box
                  className={[
                    'bulk-import-dropzone',
                    dropActive && 'active',
                    fileIssue && 'error',
                    file && !fileIssue && 'selected'
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setDropActive(true);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={() => setDropActive(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDropActive(false);
                    if (event.dataTransfer.files?.length > 1) {
                      clearOutcome();
                      setFile(null);
                      setFileIssue('Select only one CSV file at a time.');
                    } else {
                      selectFile(event.dataTransfer.files?.[0]);
                    }
                  }}
                >
                  <input
                    key={fileInputKey}
                    id="bulk-import-file"
                    className="bulk-import-file-input"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(event) => selectFile(event.target.files?.[0])}
                    disabled={isBusy}
                  />
                  <label
                    htmlFor="bulk-import-file"
                    className="bulk-import-file-label"
                  >
                    <UploadFileIcon className="bulk-import-upload-icon" />
                    <Typography variant="body2" align="center">
                      Drag and drop one CSV here, or <span>browse files</span>
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Maximum {Math.round(maximumSizeBytes / 1024)} KB
                    </Typography>
                  </label>
                </Box>

                {file && (
                  <Box className="bulk-import-selected-file">
                    <Box className="bulk-import-file-details">
                      <FileIcon color="action" />
                      <Box>
                        <Typography variant="body2">{file.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {(file.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                    </Box>
                    <Button onClick={clearFile} disabled={isBusy}>
                      Remove
                    </Button>
                  </Box>
                )}
                {fileIssue && <Alert severity="error">{fileIssue}</Alert>}

                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleValidate}
                  disabled={!canValidate}
                  className="bulk-import-primary-button"
                >
                  <BusyButtonContent busy={busyAction === 'validate'}>
                    Validate CSV
                  </BusyButtonContent>
                </Button>
              </Box>
            </Box>

            {(validation || actionIssues.length > 0) && <Divider />}

            <Box aria-live="polite" className="bulk-import-feedback">
              <IssueTable
                title="Request errors"
                issues={actionIssues}
                severity="error"
              />

              {validation && (
                <Box className="bulk-import-step">
                  <Chip label="3" className="bulk-import-step-number" />
                  <Box className="bulk-import-step-content">
                    <Typography variant="h6" component="h2">
                      Review validation
                    </Typography>
                    <Alert severity={validationIsValid ? 'success' : 'error'}>
                      {validationIsValid
                        ? 'Validation passed. Review the readable preview before confirming.'
                        : 'Nothing was imported. Correct the errors in the CSV and validate it again.'}
                    </Alert>
                    <Summary summary={validation.summary} mode="validation" />
                    <IssueTable
                      title="Errors"
                      issues={validation.errors}
                      severity="error"
                    />
                    <IssueTable
                      title="Warnings"
                      issues={validation.warnings}
                      severity="warning"
                    />
                    <PreviewTable rows={validation.preview ?? []} />

                    {validationIsValid && !result && (
                      <Box className="bulk-import-confirmation">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={confirmAcknowledged}
                              onChange={(event) =>
                                setConfirmAcknowledged(event.target.checked)
                              }
                              disabled={isBusy}
                            />
                          }
                          label={`I confirm that ${validation.summary.totalRows} new records and their listed relationships should be created.`}
                        />
                        <Button
                          variant="contained"
                          onClick={handleConfirm}
                          disabled={!canConfirm}
                          className="bulk-import-primary-button"
                        >
                          <BusyButtonContent busy={busyAction === 'confirm'}>
                            Confirm and import
                          </BusyButtonContent>
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {result && (
                <Box className="bulk-import-result" role="status">
                  <Alert
                    severity={
                      result.status === 'completed' ? 'success' : 'error'
                    }
                  >
                    {result.status === 'completed'
                      ? 'Import complete. All records and relationships were created.'
                      : 'Import failed. No records were created.'}
                  </Alert>
                  <Summary summary={result.summary} mode="import" />
                  <IssueTable
                    title="Import errors"
                    issues={result.errors}
                    severity="error"
                  />
                  <Typography variant="caption" color="textSecondary">
                    Import ID: {result.importId}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default BulkImport;
